import {
  AccountInfo,
  InteractionRequiredAuthError,
  PublicClientApplication,
} from "@azure/msal-browser";
import { loginRequest, msalConfig, tokenRequest } from "./msalConfig";
import { AuthContextType, AuthUserProfile } from "./types";
import {Roles} from "../Enums/Roles";

export const msalInstance = new PublicClientApplication(msalConfig);

let isPcaInitialized = false;
let initPromise: Promise<void> | null = null;

export async function ensureMsalInitialized(): Promise<void> {
  if (isPcaInitialized) {
    return;
  }
  if (!initPromise) {
    initPromise = (async () => {
      try {
        await msalInstance.initialize();
        const response = await msalInstance.handleRedirectPromise();
        if (response && response.account) {
          msalInstance.setActiveAccount(response.account);
        } else if (!msalInstance.getActiveAccount()) {
          const accounts = msalInstance.getAllAccounts();
          if (accounts.length > 0) {
            msalInstance.setActiveAccount(accounts[0]);
          }
        }
        isPcaInitialized = true;
      } catch (err) {
        // tslint:disable-next-line:no-console
        console.error("MSAL initialization error:", err);
        isPcaInitialized = true;
      }
    })();
  }
  return initPromise;
}

export function getActiveAccount(): AccountInfo | null {
  return msalInstance.getActiveAccount() || msalInstance.getAllAccounts()[0] || null;
}

export async function getAccessToken(scopes?: string[]): Promise<string | undefined> {
  await ensureMsalInitialized();
  const account = getActiveAccount();
  if (!account) {
    return undefined;
  }
  const request = {
    ...(scopes ? { scopes } : tokenRequest),
    account,
  };
  try {
    const response = await msalInstance.acquireTokenSilent(request);
    if (response) {
      updateAuthClient(account);
      return response.accessToken;
    }
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      try {
        const response = await msalInstance.acquireTokenPopup(request);
        if (response) {
          updateAuthClient(account);
          return response.accessToken;
        }
      } catch (popupError) {
        // tslint:disable-next-line:no-console
        console.error("MSAL popup token acquisition error:", popupError);
      }
    } else {
      // tslint:disable-next-line:no-console
      console.error("MSAL acquireTokenSilent error:", error);
    }
  }
  return undefined;
}

function extractRoles(account: AccountInfo | null): string[] {
  if (!account || !account.idTokenClaims) {
    return [];
  }
  const claims = account.idTokenClaims as Record<string, any>;
  const rolesSet = new Set<string>();

  if (Array.isArray(claims.roles)) {
    claims.roles.forEach((r: string) => rolesSet.add(r));
  }
  if (Array.isArray(claims.user_realm_roles)) {
    claims.user_realm_roles.forEach((r: string) => rolesSet.add(r));
  }
  if (Array.isArray(claims.groups)) {
    claims.groups.forEach((g: string) => rolesSet.add(g));
  }
  const hasuraRoles =
    claims["https://hasura.io/jwt/claims"]?.["x-hasura-allowed-roles"];
  if (Array.isArray(hasuraRoles)) {
    hasuraRoles.forEach((r: string) => rolesSet.add(r));
  }
  return Array.from(rolesSet);
}

function buildTokenParsed(account: AccountInfo | null): Record<string, any> | undefined {
  if (!account) {
    return undefined;
  }
  const claims = (account.idTokenClaims || {}) as Record<string, any>;
  const roles = extractRoles(account);
  const userId =
    claims.oid ||
    claims.sub ||
    account.localAccountId ||
    account.username ||
    "";
  const preferredUsername =
    claims.preferred_username || account.username || "";
  const email =
    claims.email ||
    (Array.isArray(claims.emails) ? claims.emails[0] : undefined) ||
    account.username ||
    "";
  const firstName =
    claims.given_name ||
    (account.name ? account.name.split(" ")[0] : "") ||
    account.username ||
    "";
  const lastName =
    claims.family_name ||
    (account.name ? account.name.split(" ").slice(1).join(" ") : "") ||
    "";

  const defaultRole = roles.indexOf(Roles.administrator) > -1 ? Roles.administrator :
    roles.indexOf(Roles.agency_v2) > -1 ? Roles.agency_v2 :
      roles.indexOf(Roles.operator) > -1 ? Roles.operator :
        roles.indexOf(Roles.auditor) > -1 ? Roles.auditor :
          roles.indexOf(Roles.anpgManagement) > -1 ? Roles.anpgManagement :
            roles[0] || "";

  return {
    ...claims,
    "sub": userId,
    "oid": claims.oid || userId,
    "name": account.name || claims.name || "",
    "preferred_username": preferredUsername,
    "email": email,
    "given_name": firstName,
    "family_name": lastName,
    "roles": roles,
    "user_realm_roles": roles,
    "https://hasura.io/jwt/claims": claims["https://hasura.io/jwt/claims"] || {
      "x-hasura-allowed-roles": roles,
      "x-hasura-default-role": defaultRole,
      "x-hasura-user-id": userId,
    },
  };
}

export function buildUserProfile(account: AccountInfo | null): AuthUserProfile {
  if (!account) {
    return {
      id: "",
      username: "",
      email: "",
      firstName: "",
      lastName: "",
    };
  }
  const claims = (account.idTokenClaims || {}) as Record<string, any>;
  const userId =
    claims.sub ||
    claims.oid ||
    account.localAccountId ||
    account.username ||
    "";
  const preferredUsername =
    claims.preferred_username || account.username || "";
  const email =
    claims.email ||
    (Array.isArray(claims.emails) ? claims.emails[0] : undefined) ||
    account.username ||
    "";
  const firstName =
    claims.given_name ||
    (account.name ? account.name.split(" ")[0] : "") ||
    account.username ||
    "";
  const lastName =
    claims.family_name ||
    (account.name ? account.name.split(" ").slice(1).join(" ") : "") ||
    "";

  return {
    id: userId,
    username: preferredUsername,
    email,
    firstName,
    lastName,
    enabled: true,
    emailVerified: true,
  };
}

export function hasRole(account: AccountInfo | null, role: string): boolean {
  if (!account || !role) {
    return false;
  }
  const roles = extractRoles(account);
  const normalizedTarget = role.trim().toLowerCase();
  return roles.some((r) => r.trim().toLowerCase() === normalizedTarget);
}

// Global auth client instance for non-React/synchronous access (like Apollo authLink, Axios interceptors, etc.)
export const authClient: AuthContextType = {
  authenticated: false,
  initialized: false,
  user: null,
  account: null,
  idToken: undefined,
  tokenParsed: undefined,
  idTokenParsed: undefined,
  hasRealmRole: (role: string) => hasRole(authClient.account, role),
  hasResourceRole: (role: string) => hasRole(authClient.account, role),
  loadUserProfile: async () => buildUserProfile(authClient.account),
  login: async () => {
    await ensureMsalInitialized();
    await msalInstance.loginRedirect(loginRequest);
  },
  logout: async () => {
    await ensureMsalInitialized();
    const account = getActiveAccount();
    await msalInstance.logoutRedirect({
      account: account || undefined,
      postLogoutRedirectUri: window.location.origin,
    });
  },
  getToken: async () => getAccessToken(),
  get auth() {
    return authClient;
  },
};

export function updateAuthClient(account?: AccountInfo | null) {
  const currentAccount = account !== undefined ? account : getActiveAccount();
  const tokenParsed = buildTokenParsed(currentAccount);
  const userProfile = buildUserProfile(currentAccount);

  authClient.account = currentAccount;
  authClient.authenticated = !!currentAccount;
  authClient.initialized = true;
  if (currentAccount?.idToken) {
    authClient.idToken = currentAccount.idToken;
  }
  authClient.tokenParsed = tokenParsed;
  authClient.idTokenParsed = tokenParsed;
  authClient.user = currentAccount ? userProfile : null;
}
